import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  InputSearchProps,
  SearchItem,
  NormalizedZendeskUser,
  IZendeskUser,
  IPagination,
  CustomObjectRecord,
  ProductResult,
  NotaFiscalResults,
  CustomNotaFiscalRecord,
} from "./interfaces";
import { zafClient } from "@service/zendesk";

const uniqById = <T extends { id: any }>(arr: T[]) =>
  Array.from(new Map(arr.map((u) => [u.id, u])).values());
const esc = (s: string) => s.replace(/"/g, '\\"');
const enc = (s: string) => encodeURIComponent(s);

export const normalizeUser = (u: IZendeskUser): NormalizedZendeskUser => ({
  kind: "user",
  id: u.id,
  name: u.name,
  email: u.email ?? null,
  phone: u.phone ?? null,
  cpf: u.user_fields?.cpf?.toString() ?? "",
  cnpj: u.user_fields?.cnpj_regex ?? "",
  telefone: u.phone ?? "",
  celular: u.user_fields?.celular ?? "",
  cep: u.user_fields?.cep__endereco?.toString() ?? "",
  endereco: u.user_fields?.endereco_2 ?? "",
  numero: u.user_fields?.numero?.toString() ?? "",
  complemento: u.user_fields?.complemento ?? "",
  bairro: u.user_fields?.bairro ?? "",
  cidade: u.user_fields?.cidade ?? "",
  estado: u.user_fields?.estado ?? "",
});

const requestSearchUsers = async (query: string) => {
  const res = (await zafClient.request({
    url: `/api/v2/search.json?query=${enc(query)}`,
    type: "GET",
  })) as IPagination<any>;
  const r = Array.isArray(res?.results) ? res.results : [];
  return r.filter((x: any) => x?.result_type === "user");
};

const searchUsersBasicTerm = (term: string) =>
  requestSearchUsers(`type:user ${/\s/.test(term) ? `"${esc(term)}"` : term}`);

const searchByKeyValue = async (key: string, value: string) => {
  const tries = [
    `type:user ${key}:${value}`,
    `type:user ${key}:"${esc(value)}"`,
    `type:user ${key}:${value}*`,
  ];
  for (const q of tries) {
    try {
      const users = await requestSearchUsers(q);
      if (users.length) return users;
    } catch (err) {
      console.error(err);
    }
  }
  return [];
};

export const defaultUserSearchFn = async (term: string): Promise<NormalizedZendeskUser[]> => {
  const t = (term ?? "").trim();
  if (!t) return [];

  const m = t.match(/^([^:\s]+):\s*(.+)$/);
  if (m) {
    const key = m[1];
    const val = key === "cnpj_regex" ? m[2].replace(/\D/g, "") || m[2] : m[2];
    const users = await searchByKeyValue(key, val);
    return uniqById(users).map(normalizeUser);
  }

  const basic = await searchUsersBasicTerm(t);
  const digits = t.replace(/\D/g, "");
  const byCnpj = await searchByKeyValue("cnpj_regex", digits || t);
  const byPosto = await searchByKeyValue("_assitencia_tecnica__posto", t);
  const byCpf = await searchByKeyValue("cpf", t);

  return uniqById([...basic, ...byCnpj, ...byPosto, ...byCpf]).map(normalizeUser);
};

const normalizeProduct = (r: CustomObjectRecord): ProductResult => ({
  kind: "product",
  id: r.id,
  name: r.name || r.custom_object_fields?.produto_comercial || "(sem nome)",
  custom_object_key: r.custom_object_key as "produto",
  custom_object_fields: r.custom_object_fields,
});

export const defaultProductSearchFn = async (
  term: string,
  objectTypeKey = "produto"
): Promise<ProductResult[]> => {
  const t = term?.trim();
  if (!t) return [];

  const res = (await zafClient.request({
    url: `/api/v2/custom_objects/${objectTypeKey}/records/search?query=*`,
    type: "POST",
    contentType: "application/json",
    data: JSON.stringify({
      filter: {
        $or: [
          { name: { $contains: t } },
          { "custom_object_fields.codigo_do_produto": { $contains: t } },
        ],
      },
    }),
  })) as { custom_object_records?: CustomObjectRecord[] };

  const records = res?.custom_object_records ?? [];
  return uniqById(records.map(normalizeProduct));
};

async function fetchNotaFiscalRecordsAll(objectKey: string) {
  const url = `/api/v2/custom_objects/${objectKey}/records`;
  const res = (await zafClient.request({
    url,
    type: "GET",
  })) as { custom_object_records?: CustomNotaFiscalRecord[] };
  return res?.custom_object_records ?? [];
}

export const defaultNotaFiscalSearchFn = async (
  term: string,
  objectTypeKey = "nota_fiscal"
): Promise<NotaFiscalResults[]> => {
  const t = term?.trim();
  const all = await fetchNotaFiscalRecordsAll(objectTypeKey);

  const ans: NotaFiscalResults[] = [];
  all.forEach((item) => {
    if (String(item.custom_object_fields.nota_fiscal).includes(t)) {
      ans.push({
        kind: "nota-fiscal",
        id: item.id,
        name: item.custom_object_fields.nota_fiscal,
        nameTmp: item.name,
        custom_object_key: "nota-fiscal",
        custom_object_fields: item.custom_object_fields,
      });
    }
  });

  return ans;
};

// =================== HOOK ===================

function useDebouncedCallback<T extends (...args: any[]) => any>(fn: T, delay: number) {
  const timer = useRef<number | null>(null);
  const cb = useCallback(
    (...args: Parameters<T>) => {
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => fn(...args), delay);
    },
    [fn, delay]
  );
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    []
  );
  return cb;
}

export function useUserSearch({
  searchFn,
  minChars = 2,
  debounceMs = 300,
  initialValue = "",
  onSelect,
  mode = "user",
  objectTypeKey = "produto",
}: InputSearchProps) {
  const [query, setQuery] = useState(initialValue);
  const [results, setResults] = useState<SearchItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState<number>(-1);
  const [error, setError] = useState<string | null>(null);

  const effectiveSearch = useMemo(() => {
    if (searchFn) return searchFn;
    return (term: string) =>
      mode === "product"
        ? defaultProductSearchFn(term, objectTypeKey)
        : mode === "nota-fiscal"
          ? defaultNotaFiscalSearchFn(term)
          : defaultUserSearchFn(term);
  }, [searchFn, mode, objectTypeKey]);

  const doSearch = useCallback(
    async (term: string) => {
      if (term.trim().length < minChars) {
        setResults([]);
        setOpen(false);
        setLoading(false);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      setOpen(true);
      try {
        const list = await effectiveSearch(term.trim());
        setResults(list);
        setHighlight(list.length ? 0 : -1);
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || "Falha na busca";
        setError(msg);
        setResults([]);
        setHighlight(-1);
      } finally {
        setLoading(false);
      }
    },
    [effectiveSearch, minChars]
  );

  const debouncedSearch = useDebouncedCallback(doSearch, debounceMs);

  const handleChange = (value: string) => {
    setQuery(value);
    debouncedSearch(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight >= 0 && highlight < results.length) {
        const picked = results[highlight];
        onSelect(picked);
        setQuery(picked.name);
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const handleBlur = () => setTimeout(() => setOpen(false), 120);

  const handleSelect = (item: SearchItem) => {
    onSelect(item);
    setQuery(item.name);
    setOpen(false);
  };

  return {
    query,
    results,
    open,
    loading,
    error,
    highlight,
    handleChange,
    handleKeyDown,
    handleBlur,
    handleSelect,
  };
}
